import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    InputAdornment,
    MenuItem,
    IconButton,
    Tooltip,
} from "@mui/material";
import { Search as SearchIcon, Refresh as RefreshIcon } from "@mui/icons-material";
import { listSubscriptions } from "../../api/superAdminApi";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import { formatSubscriptionDate, SERVICE_LABELS } from "./adminUtils";

const AdminSubscriptions = () => {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [serviceFilter, setServiceFilter] = useState("");
    const [expiredFilter, setExpiredFilter] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(15);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 400);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = { page: page + 1, per_page: rowsPerPage };
            if (debouncedSearch) params.search = debouncedSearch;
            if (serviceFilter) params.service = serviceFilter;
            if (expiredFilter !== "") params.expired = expiredFilter;

            const { rows: data, meta } = await listSubscriptions(params);
            setRows(data);
            setTotal(meta.total ?? data.length);
        } catch (err) {
            setError(err.message || "Failed to load subscriptions");
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, debouncedSearch, serviceFilter, expiredFilter]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(0); }, [debouncedSearch, serviceFilter, expiredFilter]);

    return (
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" sx={{ ...WB_SX.pageTitle, borderLeft: "4px solid #7c3aed", pl: 1.5, mb: 0.5 }}>
                Subscriptions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720, lineHeight: 1.65 }}>
                View subscription end dates for email, personal WhatsApp, and WhatsApp Business across all users.
            </Typography>

            {error ? <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                <TextField
                    size="small"
                    placeholder="Search by user ID, name, or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 280, ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong }}
                />
                <TextField select size="small" label="Service" value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} sx={{ minWidth: 180 }}>
                    <MenuItem value="">All services</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="personal_whatsapp">Personal WhatsApp</MenuItem>
                    <MenuItem value="whatsapp_business">WhatsApp Business</MenuItem>
                </TextField>
                <TextField select size="small" label="Status" value={expiredFilter} onChange={(e) => setExpiredFilter(e.target.value)} sx={{ minWidth: 140 }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="0">Active</MenuItem>
                    <MenuItem value="1">Expired</MenuItem>
                </TextField>
                <Tooltip title="Refresh">
                    <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                </Tooltip>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{ ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, overflow: "auto" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {["User", "Service", "Subscription end", "Days remaining", "Status"].map((h) => (
                                <TableCell key={h} sx={WB_SX.tableHeadCell}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                                    <Typography variant="body2" color="text.secondary">No subscriptions found.</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            rows.map((row, idx) => (
                                <TableRow key={`${row.user_id}-${row.service}-${idx}`} sx={WB_SX.tableRow}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.user_name || "—"}</Typography>
                                        <Typography variant="caption" color="text.secondary">#{row.user_id} · {row.user_email}</Typography>
                                    </TableCell>
                                    <TableCell>{SERVICE_LABELS[row.service] || row.service}</TableCell>
                                    <TableCell>{formatSubscriptionDate(row.subscription_end)}</TableCell>
                                    <TableCell>{row.days_remaining ?? "—"}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={row.is_expired ? "Expired" : "Active"}
                                            color={row.is_expired ? "error" : "success"}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 15, 25, 50]}
            />
        </Box>
    );
};

export default AdminSubscriptions;
